import React, { useState } from 'react';
import {
  TrashIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Custom Question Item Component
const SortableCustomQuestionItem = ({ question, index, onUpdate, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `question-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`border border-gray-200 rounded-lg p-4 ${isDragging ? 'shadow-lg' : ''}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <button
            {...attributes}
            {...listeners}
            className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
            title="Drag to reorder"
          >
            <Bars3Icon className="h-4 w-4" />
          </button>
          <span className="text-sm font-medium text-gray-700">
            Question {index + 1}
          </span>
        </div>
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="text-red-600 hover:text-red-800"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Question Text
          </label>
          <textarea
            value={question.question_text}
            onChange={(e) => onUpdate(index, 'question_text', e.target.value)}
            rows={2}
            className="w-full border-gray-300 rounded-md shadow-sm"
            placeholder="Enter your question..."
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Question Type
            </label>
            <select
              value={question.question_type}
              onChange={(e) => onUpdate(index, 'question_type', e.target.value)}
              className="w-full border-gray-300 rounded-md shadow-sm"
            >
              <option value="technical">Technical</option>
              <option value="behavioral">Behavioral</option>
              <option value="situational">Situational</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Skill Assessed
            </label>
            <input
              type="text"
              value={question.skill_assessed}
              onChange={(e) => onUpdate(index, 'skill_assessed', e.target.value)}
              className="w-full border-gray-300 rounded-md shadow-sm"
              placeholder="e.g., Problem Solving"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const CreateInterviewModal = ({ onClose, onSubmit, jobDescriptions, isLoading, defaultJobId = '' }) => {
    const [formData, setFormData] = useState({
      title: '',
      description: '',
      job_description_id: defaultJobId,
      duration_minutes: 20,
      include_ai_questions: false,
      ai_generated_questions: 5,
      include_custom_questions: false,
      custom_questions: []
    });
  
    const [showCustomQuestions, setShowCustomQuestions] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Drag and drop sensors
    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    );
  
    const handleSubmit = (e) => {
      e.preventDefault();
      
      // Prevent double submission - once clicked, stay disabled
      if (isLoading || isSubmitting) return;
      
      setIsSubmitting(true); // This will stay true until modal closes
      
      const submitData = {
        title: formData.title,
        description: formData.description,
        job_description_id: formData.job_description_id,
        duration_minutes: formData.duration_minutes,
        ai_generated_questions: formData.include_ai_questions ? formData.ai_generated_questions : 0,
        custom_questions: formData.include_custom_questions ? formData.custom_questions : []
      };
  
      onSubmit(submitData);
    };
  
    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    };
  
    const addCustomQuestion = () => {
      setFormData(prev => ({
        ...prev,
        custom_questions: [...prev.custom_questions, {
          question_text: '',
          question_type: 'technical',
          skill_assessed: ''
        }]
      }));
    };
  
    const updateCustomQuestion = (index, field, value) => {
      setFormData(prev => ({
        ...prev,
        custom_questions: prev.custom_questions.map((q, i) => 
          i === index ? { ...q, [field]: value } : q
        )
      }));
    };
  
    const removeCustomQuestion = (index) => {
      setFormData(prev => ({
        ...prev,
        custom_questions: prev.custom_questions.filter((_, i) => i !== index)
      }));
    };

    const handleDragEnd = (event) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        const oldIndex = parseInt(active.id.replace('question-', ''));
        const newIndex = parseInt(over.id.replace('question-', ''));

        setFormData(prev => ({
          ...prev,
          custom_questions: arrayMove(prev.custom_questions, oldIndex, newIndex)
        }));
      }
    };
  
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Create New Interview</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
  
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Interview Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="input-field"
                    placeholder="e.g., Software Engineer Interview"
                  />
                </div>
  
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration (minutes)
                  </label>
                  <input
                    type="number"
                    name="duration_minutes"
                    value={formData.duration_minutes}
                    onChange={handleChange}
                    min="15"
                    max="180"
                    className="input-field"
                  />
                </div>
              </div>
  
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Description
                </label>
                <select
                  name="job_description_id"
                  value={formData.job_description_id}
                  onChange={handleChange}
                  required
                  disabled={!!defaultJobId}
                  className={`input-field ${defaultJobId ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">Select a job description...</option>
                  {jobDescriptions.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.title} - {job.company}
                    </option>
                  ))}
                </select>
                {defaultJobId && (
                  <p className="text-xs text-gray-500 mt-1">
                    Interview will be created for the current job description
                  </p>
                )}
              </div>
  
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="input-field"
                  placeholder="Brief description of the interview..."
                />
              </div>
  
              {/* Question Configuration */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Question Configuration</h3>
                
                <div className="space-y-4">
                  {/* AI Questions */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="include_ai_questions"
                      checked={formData.include_ai_questions}
                      onChange={handleChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Include AI Generated Questions
                    </label>
                  </div>
  
                  {formData.include_ai_questions && (
                    <div className="ml-6">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Number of AI Questions
                      </label>
                      <input
                        type="number"
                        name="ai_generated_questions"
                        value={formData.ai_generated_questions}
                        onChange={handleChange}
                        min="1"
                        max="20"
                        className="input-field w-32"
                      />
                    </div>
                  )}
  
                  {/* Custom Questions */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="include_custom_questions"
                      checked={formData.include_custom_questions}
                      onChange={(e) => {
                        handleChange(e);
                        setShowCustomQuestions(e.target.checked);
                      }}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Include Custom Questions
                    </label>
                  </div>
                </div>
  
                {/* Custom Questions Editor */}
                {showCustomQuestions && (
                  <div className="mt-6 border-t pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-medium text-gray-900">Custom Questions</h4>
                      <button
                        type="button"
                        onClick={addCustomQuestion}
                        className="btn-primary text-sm"
                      >
                        Add Question
                      </button>
                    </div>
  
                    <div className="space-y-4 max-h-64 overflow-y-auto">
                      {formData.custom_questions.length > 0 ? (
                        <DndContext 
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleDragEnd}
                        >
                          <SortableContext 
                            items={formData.custom_questions.map((_, index) => `question-${index}`)}
                            strategy={verticalListSortingStrategy}
                          >
                            {formData.custom_questions.map((question, index) => (
                              <SortableCustomQuestionItem
                                key={`question-${index}`}
                                question={question}
                                index={index}
                                onUpdate={updateCustomQuestion}
                                onRemove={removeCustomQuestion}
                              />
                            ))}
                          </SortableContext>
                        </DndContext>
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          No custom questions added yet. Click "Add Question" to get started.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
  
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button 
                  type="button" 
                  onClick={onClose} 
                  className="btn-secondary"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading || isSubmitting}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {(isLoading || isSubmitting) ? 'Creating...' : 'Create Interview'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

export default CreateInterviewModal;